import { addYears, differenceInMonths, differenceInYears } from 'date-fns';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'domains/dates/format';
import Logo from 'assets/expressable-logo.svg?react';
import { GoalSection } from 'components/GoalSection';
import { Heading, Paragraph } from 'components/Typography';
import { loadCarePlanFromStorage } from 'domains/carePlanPDF/data/localStorage';
import { CarePlanShortTermGoalSection } from 'pages/Dashboard/components/CarePlanShortTermGoalSection';
import { DATE_SHORT_HUMAN, DAY_MONTH_YEAR, TIME_WITH_ZONE_SHORT } from 'utils/date';

/**
 * NOTE(juan): This page is not meant to be accessed by users, instead this page is meant to be accessed by our external PDF service
 * That's why you'll see many hard-coded font values and no margins
 */

/**
 * NOTE(juan): In order to get the headless chromium to properly pick up page break CSS rules, the parent div can't be a flex
 * (it must be a block element), this and other limitations are present on this. Be very careful with any changes as its very unstable
 */

export const CarePlanPDF: React.FC = () => {
  const [summary] = useState(loadCarePlanFromStorage());

  if (!summary) {
    throw new Error('Tried to access care plan PDF page but no data was found');
  }

  const { isCertifiedCarePlan = false, pcpInfo, clientInfo, goals, additionalGoals, therapistInfo, visitFrequency, diagnoses, medicalDiagnoses, primaryPurpose, areasOfFunction, developmentalDelays, barriersTocare, skilledInterventions, adaptiveEquipment, dischargePlan } = summary;
  const { t } = useTranslation();

  const clientDob = new Date(clientInfo.clientDateOfBirth);
  const clientAgeInYears = differenceInYears(new Date(), clientDob);
  const latestBirthday = addYears(clientDob, clientAgeInYears);
  const clientAgeInMonths = differenceInMonths(new Date(), latestBirthday);

  const fullTherapistName = `${therapistInfo.fullName}, ${therapistInfo.therapistCredentials}`;

  // Validate that we have required PCP info for certified care plan
  const isValidCertifiedCarePlan = isCertifiedCarePlan && pcpInfo && pcpInfo?.name;
  const currentDate = format(new Date(), DATE_SHORT_HUMAN);

  return (
    <div className="w-full">
      {/* Cover Letter Page - Only show if certified care plan with valid PCP info */}
      {isValidCertifiedCarePlan && (
        <div className="min-h-screen break-after-page">
          {/* Header with Title and Logo */}
          <header className="mb-32">
            <div className="flex items-center justify-between">
              <div className="max-w-[340px]">
                <Heading level="h4" className="font-bold leading-tight">
                  Speech Therapy Signed Plan of Care Request Form
                </Heading>
              </div>
              <Logo className="fill-current text-black-100% w-auto h-[22px]" title="Expressable" />
            </div>
          </header>

          <div className="max-w-full">
            {/* Date */}
            <Paragraph className="mb-32 text-[12pt]">{currentDate}</Paragraph>

            {/* Attention Section */}
            <Paragraph className="mb-16 text-[12pt] font-bold">ATTENTION:</Paragraph>

            {/* Doctor Name */}
            <div className="mb-32">
              <Paragraph className="mb-4 text-[12pt]">{pcpInfo!.name}</Paragraph>
            </div>

            {/* Subject */}
            <Paragraph className="mb-4 text-[12pt] font-bold">
              Subject: Speech Therapy Signed Plan of Care Request Form
            </Paragraph>
            <Paragraph className="mb-32 text-[12pt] font-light text-gray-600">URGENT CONFIDENTIAL</Paragraph>

            {/* Greeting */}
            <Paragraph className="mb-24 text-[12pt]">Dear provider,</Paragraph>

            {/* Body Paragraphs */}
            <Paragraph className="mb-24 text-[12pt]">
              We are currently working with a mutual client who is seeking speech therapy services. To secure the
              necessary approval from insurance for these services, we require a signed plan of care to be submitted to
              the health plan for authorization.
            </Paragraph>

            <Paragraph className="mb-24 text-[12pt]">
              We kindly request that you review, sign, and return the attached plan of care at your earliest
              convenience. Our fax number is 512-546-6034. Your prompt response will help us expedite the process for
              our client.
            </Paragraph>

            <Paragraph className="mb-32 text-[12pt]">
              Thank you for your attention to this matter. If you have any questions or need additional information,
              please call us at 512-768-0274 and leave a detailed message referencing the patient name.
            </Paragraph>

            {/* Signature */}
            <div className="mb-32">
              <Paragraph className="mb-4 text-[12pt]">Best regards,</Paragraph>
              <Paragraph className="text-[12pt]">Expressable Speech Therapy</Paragraph>
            </div>
          </div>
        </div>
      )}

      {/* Care Plan Content - Always show, but add page break if certified care plan */}
      <div className={isValidCertifiedCarePlan ? 'break-before-page' : ''}>
        <header className="flex justify-between items-center mb-24">
          <Heading level="h2">{t(`pdfs.carePlan.title`)}</Heading>

          <Logo className="fill-current text-black-100% w-auto h-[22px]" title="Expressable" />
        </header>
        <Heading level="h6" className="mb-24">
          {t(`pdfs.carePlan.clientDetails.title`)}
        </Heading>
        <section className="grid grid-cols-3 gap-y-24 gap-x-12">
          <LabelValuePair label={t(`pdfs.carePlan.clientInfo.clientFirstName`)} value={clientInfo.clientFirstName} />
          <LabelValuePair label={t(`pdfs.carePlan.clientInfo.clientLastName`)} value={clientInfo.clientLastName} />
          <LabelValuePair
            label={t(`pdfs.carePlan.clientInfo.clientDateOfBirth`)}
            value={`${format(new Date(clientInfo.clientDateOfBirth), DATE_SHORT_HUMAN)} (${clientAgeInYears} ${t(
              `pdfs.carePlan.clientInfo.clientDateOfBirth.year${clientAgeInYears > 1 ? 's' : ''}`,
            )} ${clientAgeInMonths} ${t(
              `pdfs.carePlan.clientInfo.clientDateOfBirth.month${clientAgeInMonths > 1 ? 's' : ''}`,
            )})`}
          />
          <LabelValuePair label={t(`pdfs.carePlan.clientInfo.therapistName`)} value={fullTherapistName} />
          <LabelValuePair
            label={t(`pdfs.carePlan.clientInfo.frequency.title`)}
            value={t('pdfs.carePlan.clientInfo.frequency', {
              count: visitFrequency.sessionsCount,
              frequency: t(`pdfs.carePlan.clientInfo.frequency.period.${visitFrequency.frequency}`),
            })}
          />
          <LabelValuePair label={t(`pdfs.carePlan.clientInfo.reportDate`)} value={format(new Date(), DATE_SHORT_HUMAN)} />
        </section>
        <hr className="h-2 bg-black-200 w-full my-24" />

        <Heading level="h6" className="mb-24">
          {t(`pdfs.carePlan.diagnosis.title`)}
        </Heading>
        <section className="flex flex-col gap-24">
          {diagnoses.map(diagnosis => (
            <LabelValuePair label={diagnosis.diagnosisCode} value={diagnosis.description} key={diagnosis.description} />
          ))}

          {medicalDiagnoses.map(diagnosis => (
            <LabelValuePair label={diagnosis.code} value={diagnosis.description} key={diagnosis.description} />
          ))}
        </section>
        <hr className="h-2 bg-black-200 w-full my-24" />

        <Heading level="h6" className="mb-24">
          {t(`pdfs.carePlan.faqs.title`)}
        </Heading>
        <section className="flex flex-col gap-24">
          <LabelValuePair label={t('pdfs.carePlan.faqs.1.title')} value={t('pdfs.carePlan.faqs.1.description')} />
          <LabelValuePair label={t('pdfs.carePlan.faqs.2.title')} value={t('pdfs.carePlan.faqs.2.description')} />
          <LabelValuePair label={t('pdfs.carePlan.faqs.3.title')} value={t('pdfs.carePlan.faqs.3.description')} />
        </section>

        <Heading level="h6" className="mb-24 break-before-page">
          {t(`pdfs.carePlan.goals.title`)}
        </Heading>
        {goals.map((goal, i) => (
          <GoalSection
            defaultOpen
            goal={goal}

            key={`${goal.goalContext}-${i}`}
            pdf
            shortTermGoalComponent={CarePlanShortTermGoalSection}
          />
        ))}

        <hr className="h-2 bg-black-200 w-full my-24" />

        <Heading level="h6" className="mb-24">
          {t(`pdfs.carePlan.additionalGoals.title`)}
        </Heading>
        <section className="grid grid-cols-3 gap-24">
          {additionalGoals.map(goal => (
            <React.Fragment key={goal.goalID}>
              <Paragraph className="mb-16 text-[12pt] font-bold">{goal.goalType}</Paragraph>
              <Paragraph className="mb-16 text-[12pt] col-span-2">{goal.detail.goalDescription}</Paragraph>
            </React.Fragment>
          ))}
        </section>

        <hr className="h-2 bg-black-200 w-full my-24" />

        {/* Primary Purpose of Therapy */}
        {primaryPurpose && (
          <div className="break-inside-avoid">
            <Heading level="h6" className="mb-24">
              Primary Purpose of Therapy
            </Heading>
            <section className="flex flex-col gap-24">
              <Paragraph className="mb-16 text-[12pt]">{primaryPurpose.details}</Paragraph>
            </section>
            <hr className="h-2 bg-black-200 w-full my-24" />
          </div>
        )}

        {/* Treatment Focused on Areas of Function */}
        {areasOfFunction && areasOfFunction.length > 0 && (
          <div className="break-inside-avoid">
            <Heading level="h6" className="mb-24">
              Treatment Focused on Areas of Function
            </Heading>
            <section className="flex flex-col gap-24">
              {areasOfFunction.map((area: string, index: number) => (
                <Paragraph key={index} className="mb-16 text-[12pt]">• {area}</Paragraph>
              ))}
            </section>
            <hr className="h-2 bg-black-200 w-full my-24" />
          </div>
        )}

        {/* Developmental Delays or Chronic Disabilities */}
        <div className="break-inside-avoid">
          <Heading level="h6" className="mb-24">
            Developmental Delays or Chronic Disabilities
          </Heading>
          <section className="flex flex-col gap-24">
            {developmentalDelays ? (
              <>
                <LabelValuePair
                  label="Present"
                  value={developmentalDelays.present ? 'Yes' : 'No'}
                />
                {developmentalDelays.severity && (
                  <LabelValuePair
                    label="Details"
                    value={developmentalDelays.severity}
                  />
                )}
              </>
            ) : (
              <Paragraph className="mb-16 text-[12pt]">No developmental delays or chronic disabilities</Paragraph>
            )}
          </section>
          <hr className="h-2 bg-black-200 w-full my-24" />
        </div>

        {/* Barriers to Care */}
        {barriersTocare && barriersTocare.length > 0 && (
          <div className="break-inside-avoid">
            <Heading level="h6" className="mb-24">
              Barriers to Care
            </Heading>
            <section className="flex flex-col gap-24">
              {barriersTocare.map((barrier: string, index: number) => (
                <Paragraph key={index} className="mb-16 text-[12pt]">• {barrier}</Paragraph>
              ))}
            </section>
            <hr className="h-2 bg-black-200 w-full my-24" />
          </div>
        )}

        {/* Skilled Interventions that may be used */}
        {skilledInterventions && skilledInterventions.length > 0 && (
          <div className="break-inside-avoid">
            <Heading level="h6" className="mb-24">
              Skilled Interventions that may be used
            </Heading>
            <section className="flex flex-col gap-24">
              {skilledInterventions.map((intervention: string, index: number) => (
                <Paragraph key={index} className="mb-16 text-[12pt]">• {intervention}</Paragraph>
              ))}
            </section>
            <hr className="h-2 bg-black-200 w-full my-24" />
          </div>
        )}

        {/* Adaptive Equipment Or Assistive Devices */}
        {adaptiveEquipment && adaptiveEquipment.length > 0 && (
          <div className="break-inside-avoid">
            <Heading level="h6" className="mb-24">
              Adaptive Equipment Or Assistive Devices
            </Heading>
            <section className="flex flex-col gap-24">
              {adaptiveEquipment.map((equipment: string, index: number) => (
                <Paragraph key={index} className="mb-16 text-[12pt]">• {equipment}</Paragraph>
              ))}
            </section>
            <hr className="h-2 bg-black-200 w-full my-24" />
          </div>
        )}

        {/* Discharge Plan */}
        {dischargePlan && (
          <div className="break-inside-avoid">
            <Heading level="h6" className="mb-24">
              Discharge Plan
            </Heading>
            <section className="flex flex-col gap-24">
              <Paragraph className="mb-16 text-[12pt]">{dischargePlan.details}</Paragraph>
            </section>
            <hr className="h-2 bg-black-200 w-full my-24" />
          </div>
        )}

        {/* PCP */}
        {isValidCertifiedCarePlan && (
          <div className="break-inside-avoid mb-24">
            <Heading level="h6" className="mb-40">
              PCP
            </Heading>

            <div className="grid grid-cols-1 gap-32 max-w-2xl">
              {/* PCP Signature Field */}
              <div className="flex flex-col">
                <div className="mb-8 border-b border-black h-8 max-w-sm"></div>
                <Paragraph className="text-[12pt] text-black-600">PCP Signature</Paragraph>
              </div>

              {/* PCP NPI Field */}
              <div className="flex flex-col">
                <div className="mb-8 border-b border-black h-8 max-w-sm"></div>
                <Paragraph className="text-[12pt] text-black-600">PCP NPI</Paragraph>
              </div>

              {/* Date Field */}
              <div className="flex flex-col">
                <div className="mb-8 border-b border-black h-8 max-w-sm"></div>
                <Paragraph className="text-[12pt] text-black-600">Date</Paragraph>
              </div>
            </div>
            <hr className="h-2 bg-black-200 w-full my-24" />
          </div>
        )}

        <Paragraph className="mb-16 text-[24pt] font-signature">{fullTherapistName}</Paragraph>
        <Paragraph className="mb-16 text-[10pt] text-black-600">
          {t('pdfs.carePlan.signature.description', {
            name: fullTherapistName,
            date: format(new Date(), `${DAY_MONTH_YEAR} 'at' ${TIME_WITH_ZONE_SHORT}`),
          })}
        </Paragraph>
      </div>
    </div>
  );
};

const LabelValuePair: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  return (
    <div className="break-inside-avoid">
      <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
        {label}
      </Heading>
      <Paragraph className="mb-16 text-[12pt]">{value}</Paragraph>
    </div>
  );
};
